import React, { useState, useEffect } from "react";
import Wordmark from "../components/Wordmark";
import { PickleballBall } from "../SportIcons";
import { collection, query, where, getDocs, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { signInAnonymously, onAuthStateChanged, getAuth } from "firebase/auth";
import { db, app, clubSignInFn, createQuickEventFn, signInWithClubToken } from "../firebase";
import { Club, Player, AuthSession } from "../types";
import { FaqChat, LANDING_TEXT } from "../LandingPage";
import { passkeySupported, registerPasskey, loginWithPasskey, passkeyErrorMessage } from "./passkey";

// ── Tyylit ──────────────────────────────────────────────────
const C = {
  card: "var(--sl-card)",
  navy:  "var(--sl-primary)",
  gold:  "var(--sl-accent)",
  text:  "var(--sl-text)",
  muted: "var(--sl-muted)",
  bg:    "var(--sl-bg)",
  white: "#ffffff",
  border:"var(--sl-border)",
  error: "var(--sl-error)",
};
const F = { heading: "'Rajdhani', sans-serif", body: "'Sora', sans-serif" };

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "14px 16px", boxSizing: "border-box",
  background: C.card, border: `2px solid ${C.border}`,
  borderRadius: 12, fontSize: 16, color: C.text,
  fontFamily: F.body, outline: "none", marginBottom: 12,
};
// Ensisijainen nappi samaa neonkeltaista kuin etusivulla.
const btnStyle: React.CSSProperties = {
  width: "100%", padding: "15px 0", borderRadius: 12, border: "none",
  background: "#d9ff3a", boxShadow: "0 8px 22px rgba(160,190,20,0.35)",
  color: "#131a05", fontSize: 16, fontWeight: 700,
  fontFamily: F.heading, cursor: "pointer", letterSpacing: 0.5,
};
const btnSecondary: React.CSSProperties = {
  ...btnStyle,
  background: C.bg, color: C.navy, boxShadow: "none",
  border: `2px solid ${C.border}`,
};

// ── Vaiheet ─────────────────────────────────────────────────
type Step =
  | "club_code"       // Syötä klubikoodi
  | "role_select"     // Admin vai pelaaja?
  | "admin_pin"       // Admin PIN
  | "event_pin"       // Pelaaja: tapahtuman PIN
  | "player_name"     // Pelaaja: nimi (uusi)
  | "player_bio"      // Pelaaja: biometria (paluu)
  | "super_admin"     // Super admin -hallinta
  | "quick_event"     // Kaveriporukka: luo pikatapahtuma
  | "name_login";     // Tunnistettu pelaaja: kirjaudu nimellä ilman PIN

// ── Nimien vertailu ───────────────────────────────────────────
//
// Nimi kirjoitetaan kasin, joten se on melkein aina hieman eri kuin
// klubin kirjoissa: lempinimi, pelkka etunimi, iso alkukirjain
// unohtuneena, ylimaarainen valilyonti. Aiemmin tasmallinen vertailu
// teki jokaisesta poikkeamasta uuden pelaajan ilman ettei kukaan
// huomannut mitaan, ja klubiin kertyi kaksoiskappaleita.

/** Vertailumuoto: reunavalit pois, pienet kirjaimet, valit yhdeksi. */
function normName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Levenshtein-etaisyys. Nimet ovat lyhyita, joten tama riittaa. */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = cur;
  }
  return prev[b.length];
}

export type ClubPlayer = { id: string; name: string };

/**
 * Klubin pelaajat, jotka voisivat olla sama henkilo kuin kirjoitettu
 * nimi. Jarjestys parhaasta alkaen; nayttoon otetaan vain muutama.
 *
 * "Jukka" osuu nimeen "Jukka Partinen", koska sana alkaa kirjoitetulla.
 * "Mikko Tikala" osuu nimeen "Mikko Tikkala" kirjoitusvirheen yli.
 * "Tikis" ei osu mihinkaan — lempinimea ei voi paatella, ja siksi uuden
 * pelaajan luonti vaatii erillisen vahvistuksen.
 */
function suggestPlayers(input: string, players: ClubPlayer[]): ClubPlayer[] {
  const q = normName(input);
  if (q.length < 2) return [];
  const hits: { p: ClubPlayer; rank: number }[] = [];
  for (const p of players) {
    const n = normName(p.name);
    if (!n || n === q) continue;
    let rank: number | null = null;
    if (n.split(" ").some((w) => w.startsWith(q))) rank = 0;
    else if (n.includes(q)) rank = 1;
    else {
      const d = editDistance(q, n);
      if (d <= (q.length <= 5 ? 1 : 2)) rank = 2 + d;
    }
    if (rank !== null) hits.push({ p, rank });
  }
  return hits.sort((a, b) => a.rank - b.rank).slice(0, 4).map((h) => h.p);
}

// ── Firebase Auth varmistus ───────────────────────────────────
async function ensureAuth(): Promise<void> {
  const auth = getAuth(app);
  if (auth.currentUser) return;
  await signInAnonymously(auth);
  await new Promise<void>((resolve) => {
    const unsub = onAuthStateChanged(auth, (u) => { if (u) { unsub(); resolve(); } });
  });
}

// ── Kirjautumisvirheet ────────────────────────────────────────
// Palvelin erottaa väärän PIN:n ja arvailujarrun toisistaan. Käyttäjän
// pitää tietää kummasta on kyse, muuten lukitus näyttää rikkinäiseltä.
function signInError(err: unknown, lang: "fi" | "en"): string {
  // Laitteen WebAuthn-virheellä ei ole Firebasen code-kenttää vaan
  // DOMExceptionin name. Ilman tätä haaraa jokainen niistä päätyi alimmalle
  // riville ja näytti tekstin "Verkkovirhe", joka ei kerro mitään — juuri se
  // teki Androidin passkey-vian jäljittämisestä mahdotonta.
  const passkeyMsg = passkeyErrorMessage(err, lang);
  if (passkeyMsg) return passkeyMsg;

  const code = (err as { code?: string })?.code || "";
  if (code.includes("resource-exhausted")) {
    return lang === "fi"
      ? "Liian monta yritystä. Odota noin 15 minuuttia."
      : "Too many attempts. Please wait about 15 minutes.";
  }
  if (code.includes("permission-denied")) {
    return lang === "fi" ? "Väärä PIN." : "Incorrect PIN.";
  }
  if (code.includes("not-found")) {
    return lang === "fi" ? "Ei löytynyt. Tarkista koodi." : "Not found. Check the code.";
  }
  return lang === "fi" ? "Verkkovirhe. Yritä uudelleen." : "Network error. Please try again.";
}

// ── Passkey ───────────────────────────────────────────────────
//
// Aiempi toteutus kutsui navigator.credentials-rajapintaa suoraan eikä
// tarkistanut mitään palvelimella. Nyt haaste tulee palvelimelta ja
// allekirjoitus tarkistetaan siellä — ks. src/auth/passkey.ts ja
// functions/src/passkey.ts.
//
// localStorageen jää vain nimi tervehdystä varten. Itse tunniste asuu
// laitteen avainnipussa ja julkinen avain palvelimella, joten täällä ei
// ole mitään salaista eikä mitään menetettävää.
const PASSKEY_NAME_KEY = "spinloop_passkey_name";

function getPasskeyName(): string | null {
  try { return localStorage.getItem(PASSKEY_NAME_KEY); } catch { return null; }
}
function setPasskeyName(name: string): void {
  try { localStorage.setItem(PASSKEY_NAME_KEY, name); } catch { /* yksityinen tila */ }
}

// Rekisteroinnin epaonnistuminen tapahtuu heti onnistuneen kirjautumisen
// jalkeen, jolloin tama nakyma purkautuu saman tien — tilamuuttuja ei siis
// ehtisi nakya. Syy sailotaan ja naytetaan seuraavalla kirjautumisruudulla,
// juuri silloin kun kayttaja ihmettelee miksei passkey-nappi toimi.
const PASSKEY_ISSUE_KEY = "spinloop_passkey_issue";

function getPasskeyIssue(): string | null {
  try { return localStorage.getItem(PASSKEY_ISSUE_KEY); } catch { return null; }
}
function setPasskeyIssue(msg: string | null): void {
  try {
    if (msg) localStorage.setItem(PASSKEY_ISSUE_KEY, msg);
    else localStorage.removeItem(PASSKEY_ISSUE_KEY);
  } catch { /* yksityinen tila */ }
}

// ── Props ────────────────────────────────────────────────────
interface LoginFlowProps {
  onSuccess: (session: AuthSession) => void;
  onSuperAdmin: () => void;
  /**
   * Kutsulinkin `?club=`-parametri. Tapahtumakutsun sähköposti johti ennen
   * pelkkään etusivuun, jossa pelaajaa tarjottiin asentamaan PWA — klubikoodi
   * oli samassa viestissä mutta se piti naputella käsin. Kun tämä on annettu,
   * klubi haetaan valmiiksi eikä laskeutumissivua näytetä lainkaan.
   */
  initialClubCode?: string;
}

// ── Komponentti ──────────────────────────────────────────────
export default function LoginFlow({ onSuccess, onSuperAdmin, initialClubCode }: LoginFlowProps) {
  const [step, setStep]           = useState<Step>("club_code");
  const [clubCode, setClubCode]   = useState(initialClubCode ?? "");
  const [club, setClub]           = useState<Club | null>(null);
  const [adminPin, setAdminPin]   = useState("");
  const [eventPin, setEventPin]   = useState("");
  const [playerName, setPlayerName] = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [foundEvent, setFoundEvent]     = useState<{ id: string; panos: number; venue: string } | null>(null);
  // Klubin pelaajat nimiehdotuksia varten. Haetaan vasta kun tapahtuman
  // PIN on hyvaksytty, eli kutsujalla on jo lukuoikeus kokoelmaan.
  const [clubPlayers, setClubPlayers] = useState<ClubPlayer[]>([]);
  // Uuden pelaajan luonti vaatii erillisen vahvistuksen, jottei
  // kirjoitusvirhe tai lempinimi tee sita huomaamatta.
  const [confirmNewPlayer, setConfirmNewPlayer] = useState(false);

  // Passkey-nappi näytetään vasta kun laitteen tuki on varmistettu.
  // Natiiviapissa tuki syntyy vasta kun WKWebView-paikkaus on asennettu,
  // joten tätä ei voi päätellä synkronisesti window.PublicKeyCredentialista.
  const [passkeyReady, setPasskeyReady] = useState(false);
  const [passkeyIssue] = useState<string | null>(() => getPasskeyIssue());
  const [passkeyName]                   = useState(() => getPasskeyName());

  useEffect(() => {
    let alive = true;
    passkeySupported().then((ok) => { if (alive) setPasskeyReady(ok); });
    return () => { alive = false; };
  }, []);

  // Virheilmoitus kuuluu siihen näkymään jossa se syntyi. Ilman tätä
  // "Väärä PIN." jää roikkumaan kaveriporukan lomakkeelle asti, jolla ei
  // ole PIN-koodin kanssa mitään tekemistä. Yksikään käsittelijä ei aseta
  // virhettä ja vaihda vaihetta samalla kertaa, joten tämä ei niele
  // yhtään oikeaa ilmoitusta.
  useEffect(() => { setError(""); }, [step]);

  // ── Vaihe 1: Klubi-koodi ─────────────────────────────────
  async function handleClubCode(e: React.FormEvent) {
    e.preventDefault();
    await lookupClub(clubCode);
  }

  // Erotettu lomakkeesta, jotta kutsulinkki voi hakea klubin ilman
  // submit-tapahtumaa. Sama polku molemmille — linkki ei saa ohittaa
  // yhtäkään tarkistusta, jonka käsin syötetty koodi käy läpi.
  //
  // Funktiomäärittely eikä useCallback: `lang` alustetaan vasta alempana,
  // ja riippuvuuslista luettaisiin jo renderöinnissä ennen sitä.
  async function lookupClub(raw: string) {
    setError("");
    const code = raw.trim().toUpperCase();

    // Super admin tunnistautuu omalla PIN-koodillaan; pelkkä koodi ei riitä.
    if (code === "SUPER") { setStep("super_admin"); return; }

    // Pelkkiä numeroita: käyttäjä kirjoitti PIN-koodin klubikoodin kenttään.
    // Näin kävi 12.10.2026, ja sovellus vastasi "Verkkovirhe". Kerrotaan
    // suoraan mitä tehdä, palvelinta kysymättä.
    if (/^\d{3,6}$/.test(code)) {
      setError(lang === "fi"
        ? "Tämä näyttää PIN-koodilta. Kirjoita ensin klubin koodi (esim. WANHAT). PIN kysytään seuraavaksi."
        : "This looks like a PIN. Enter the club code first (e.g. WANHAT). The PIN is asked next.");
      return;
    }

    setLoading(true);
    try {
      await ensureAuth();
      // Klubihaku tehdään palvelimella: klubidokumentit eivät ole
      // asiakkaan luettavissa ennen kuin kirjautuminen on onnistunut.
      const res = await clubSignInFn({ clubCode: code });
      const found = res.data.club;
      if (!found) { setError(lang === "fi" ? "Klubia ei löydy. Tarkista koodi." : "Club not found. Check the code."); setLoading(false); return; }
      const c = found as unknown as Club;
      setClub(c);
      setStep("role_select");
    } catch (err) {
      // Palvelin vastaa tuntemattomaan koodiin not-found-virheellä. Aiemmin
      // kaikki virheet näytettiin "Verkkovirheenä", mikä ohjasi kokeilemaan
      // samaa uudelleen eikä tarkistamaan koodia.
      const code = (err as { code?: string })?.code || "";
      setError(code.includes("not-found")
        ? (lang === "fi" ? "Klubia ei löydy. Tarkista klubin koodi." : "Club not found. Check the club code.")
        : signInError(err, lang));
    }
    setLoading(false);
  }

  // Kutsulinkki hakee klubin kerran, heti kun näkymä avautuu.
  const autoLookedUp = React.useRef(false);
  useEffect(() => {
    if (!initialClubCode || autoLookedUp.current) return;
    autoLookedUp.current = true;
    lookupClub(initialClubCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialClubCode]);

  // ── Vaihe 2b: Admin PIN ──────────────────────────────────
  async function handleAdminPin(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!club) return;
    setLoading(true);
    try {
      // PIN tarkistetaan palvelimella. Onnistuessa saadaan token, jonka
      // claimit avaavat tämän klubin dokumentit Firestore-säännöissä.
      const res = await clubSignInFn({ clubCode: club.clubCode, adminPin: adminPin.trim() });
      if (!res.data.token) { setError(lang === "fi" ? "Väärä PIN." : "Incorrect PIN."); setLoading(false); return; }
      await signInWithClubToken(res.data.token);
    } catch (err) {
      setError(signInError(err, lang));
      setLoading(false);
      return;
    }
    setLoading(false);

    localStorage.setItem("spinloop_club_name", club.name);
    localStorage.setItem("spinloop_club_code", club.clubCode);
    onSuccess({
      role: "club_admin",
      clubId: club.id,
      clubCode: club.clubCode,
      clubName: club.name,
    });
  }

  // ── Super admin ──────────────────────────────────────────
  async function handleSuperAdminPin(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await clubSignInFn({ superAdminPin: adminPin.trim() });
      if (!res.data.token) { setError(lang === "fi" ? "Väärä PIN." : "Incorrect PIN."); setLoading(false); return; }
      await signInWithClubToken(res.data.token);
      setLoading(false);
      onSuperAdmin();
    } catch (err) {
      setError(signInError(err, lang));
      setLoading(false);
    }
  }

  // ── Vaihe 2c: Tapahtuman PIN ─────────────────────────────
  async function handleEventPin(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!club) return;
    setLoading(true);
    try {
      // Tapahtuman PIN tarkistetaan palvelimella samasta syystä kuin
      // admin-PIN: tapahtumat eivät ole luettavissa ennen kirjautumista.
      const res = await clubSignInFn({ clubCode: club.clubCode, eventPin: eventPin.trim() });
      const ev = res.data.event;
      if (!ev || !res.data.token) { setError(lang === "fi" ? "Tapahtumaa ei löydy tällä PIN-koodilla." : "No event found with this PIN code."); setLoading(false); return; }
      await signInWithClubToken(res.data.token);
      setFoundEvent(ev);

      // Nimiehdotuksia varten. Klubit ovat pieniä (kymmeniä pelaajia),
      // joten koko lista kannattaa hakea kerralla. Tämä ei avaa mitään
      // uutta: sama kokoelma oli jo luettavissa tässä vaiheessa.
      try {
        const ps = await getDocs(collection(db, "clubs", club.id, "players"));
        setClubPlayers(
          ps.docs
            .map((d) => ({ id: d.id, name: (d.data().name as string) || "" }))
            .filter((p) => p.name)
        );
      } catch {
        // Ilman listaa ehdotuksia ei näytetä; kirjautuminen toimii yhä.
      }

      setStep("player_name");
    } catch (err) {
      const code = (err as { code?: string })?.code || "";
      setError(code.includes("not-found")
        ? (lang === "fi" ? "PIN-koodilla ei löydy tapahtumaa. Tarkista koodi kutsusta." : "No event with this PIN. Check the code in your invitation.")
        : signInError(err, lang));
    }
    setLoading(false);
  }

  // ── Vaihe 3: Pelaajan nimi & kirjautuminen ────────────────
  /**
   * Lomakkeen lähetys. Päättää vain kenenä liitytään; itse liittymisen
   * hoitaa joinEvent.
   *
   * Kolme tapausta. Sama nimi kirjoitusasusta riippumatta on sama
   * pelaaja. Tuntematon nimi, jolle löytyy samankaltaisia, jää
   * odottamaan että käyttäjä joko valitsee ehdotuksen tai vahvistaa
   * olevansa uusi. Vasta vahvistettu tuntematon nimi luo uuden kortin.
   */
  async function handlePlayerName(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!club || !foundEvent) return;
    const name = playerName.trim();
    if (!name) { setError(lang === "fi" ? "Syötä nimesi." : "Please enter your name."); return; }

    const exact = clubPlayers.find((p) => normName(p.name) === normName(name));
    if (exact) { await joinEvent(exact.id, exact.name); return; }

    // Tuntematon nimi. Ensimmäisellä painalluksella näytetään
    // ehdotukset ja kysytään varmistus; toisella luodaan.
    if (!confirmNewPlayer) { setConfirmNewPlayer(true); return; }
    await joinEvent(null, name);
  }

  /**
   * Liittää pelaajan tapahtumaan ja avaa istunnon.
   * @param existingId Olemassa olevan pelaajan tunnus, tai null jos luodaan uusi.
   * @param name Nimi, jolla osallistutaan.
   */
  async function joinEvent(existingId: string | null, name: string) {
    if (!club || !foundEvent) return;
    setLoading(true);

    try {
      let playerId: string;

      if (existingId) {
        playerId = existingId;
        await updateDoc(doc(db, "clubs", club.id, "players", playerId), {
          lastSeen: new Date().toISOString(),
        });
      } else {
        // Uusi pelaaja
        const newRef = doc(collection(db, "clubs", club.id, "players"));
        playerId = newRef.id;
        await setDoc(newRef, {
          name,
          ratings: {},
          createdAt: new Date().toISOString(),
          lastSeen: new Date().toISOString(),
        });
      }

      // Lisää participant eventtiin jos ei jo ole
      const partRef = doc(db, "clubs", club.id, "events", foundEvent.id, "participants", playerId);
      const partSnap = await getDoc(partRef);
      if (!partSnap.exists()) {
        await setDoc(partRef, {
          playerId,
          displayName: name,
          confirmed: true,
          paid: false,
          // Pelaajan oma liittyminen: laukaisee ilmoituksen ylläpitäjälle
          // (sendParticipantPush). Ilman tätä ilmoitus tuli sovelluksessa
          // ilmoittautuneista mutta ei PIN-koodilla liittyneistä.
          source: "player",
          createdAt: new Date().toISOString(),
        });
      } else {
        await updateDoc(partRef, { confirmed: true, source: "player" });
      }

      // Rekisteröi passkey seuraavaa kertaa varten. Julkinen avain menee
      // palvelimelle, yksityinen jää laitteen avainnippuun ja synkronoituu
      // iCloudin tai Google-salasanahallinnan kautta käyttäjän muille
      // laitteille. Epäonnistuminen ei estä kirjautumista — PIN toimii yhä.
      const pk = await registerPasskey(playerId, name);
      if (pk.ok) {
        setPasskeyName(name);
        setPasskeyIssue(null);
      } else if (pk.error) {
        // Kirjautuminen on jo onnistunut, joten tämä ei ole este. Syy
        // säilötään seuraavaa kirjautumisruutua varten.
        setPasskeyIssue(pk.error);
      }

      localStorage.setItem("spinloop_club_name", club.name);
      onSuccess({
        role: "player",
        clubId: club.id,
        clubCode: club.clubCode,
        clubName: club.name,
        playerId,
        playerName: name,
        eventId: foundEvent.id,
      });
    } catch (err) { setError(lang === "fi" ? "Virhe kirjautumisessa. Yritä uudelleen." : "Sign-in error. Please try again."); }
    setLoading(false);
  }

  // ── Vaihe: Passkey-kirjautuminen ────────────────────────────
  //
  // Klubikoodia ei tarvita: haku on discoverable, eli laite tarjoaa itse
  // tallennetut tunnisteet ja palvelin päättelee klubin allekirjoituksesta.
  async function handlePasskeyLogin() {
    setLoading(true);
    setError("");
    try {
      const result = await loginWithPasskey();
      if (!result || !result.token || !result.club || !result.playerId) {
        setError(lang === "fi"
          ? "Tunnistautuminen ei onnistunut. Käytä PIN-koodia."
          : "Authentication failed. Please use the PIN code.");
        setLoading(false);
        return;
      }

      // Token ensin: ilman sitä seuraavat luvut kaatuvat sääntöihin.
      await signInWithClubToken(result.token);

      const c = result.club;
      const playerId = result.playerId;
      const playerName = result.playerName || "";
      setPasskeyName(playerName);

      await updateDoc(doc(db, "clubs", c.id, "players", playerId), {
        lastSeen: new Date().toISOString(),
      }).catch(() => undefined);

      // Avoin tapahtuma on mukavuus, ei ehto: ilman sitä kirjaudutaan
      // klubinäkymään ja tapahtuman voi valita siellä.
      let eventId: string | undefined;
      try {
        const evSnap = await getDocs(
          query(collection(db, "clubs", c.id, "events"),
            where("status", "in", ["active", "paused"]))
        );
        if (!evSnap.empty) {
          eventId = evSnap.docs[0].id;
          const partRef = doc(db, "clubs", c.id, "events", eventId, "participants", playerId);
          const partSnap = await getDoc(partRef);
          if (!partSnap.exists()) {
            await setDoc(partRef, {
              playerId, displayName: playerName,
              confirmed: true, paid: false, source: "player",
              createdAt: new Date().toISOString(),
            });
          } else {
            await updateDoc(partRef, { confirmed: true, source: "player" });
          }
        }
      } catch { /* tapahtuma ei pakollinen */ }

      localStorage.setItem("spinloop_club_name", c.name);
      localStorage.setItem("spinloop_club_code", c.clubCode);
      onSuccess({
        // Varmennettu passkey voi tuoda järjestäjän roolin; palvelin päättää.
        role: result.role === "organizer" ? "organizer" : "player",
        clubId: c.id,
        clubCode: c.clubCode,
        clubName: c.name,
        playerId,
        playerName,
        eventId,
      });
    } catch (err) {
      setError(signInError(err, lang));
    }
    setLoading(false);
  }

  // ── Apufunktio: kirjaudu sisään tunnistetulla pelaajalla ──
  async function loginRecognizedPlayer(playerId: string, name: string) {
    if (!club) return;

    // Päivitä lastSeen
    await updateDoc(doc(db, "clubs", club.id, "players", playerId), {
      lastSeen: new Date().toISOString(),
    });

    // Etsi avoin (active tai paused) tapahtuma
    const evSnap = await getDocs(
      query(collection(db, "clubs", club.id, "events"),
        where("status", "in", ["active", "paused"]))
    );

    let eventId: string | undefined;
    if (!evSnap.empty) {
      const ev = evSnap.docs[0];
      eventId = ev.id;
      // Lisää participant jos ei jo ole
      const partRef = doc(db, "clubs", club.id, "events", ev.id, "participants", playerId);
      const partSnap = await getDoc(partRef);
      if (!partSnap.exists()) {
        await setDoc(partRef, {
          playerId, displayName: name,
          confirmed: true, paid: false, source: "player",
          createdAt: new Date().toISOString(),
        });
      } else {
        await updateDoc(partRef, { confirmed: true, source: "player" });
      }
    }

    localStorage.setItem("spinloop_club_name", club.name);
    onSuccess({
      role: "player",
      clubId: club.id,
      clubCode: club.clubCode,
      clubName: club.name,
      playerId,
      playerName: name,
      eventId,
    });
  }

  // ── Vaihe: Kirjaudu nimellä (kutsuttu pelaaja, eri laite) ──
  async function handleNameLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!club) return;
    const name = playerName.trim();
    if (!name) return;
    setLoading(true);
    setError("");
    try {
      const snap = await getDocs(
        query(collection(db, "clubs", club.id, "players"), where("name", "==", name))
      );
      if (snap.empty) {
        setError(lang === "fi"
          ? "Nimeä ei löydy järjestelmästä. Tarkista kirjoitusasu tai pyydä admin lisäämään sinut."
          : "Name not found in the system. Check spelling or ask the admin to add you.");
        setLoading(false);
        return;
      }
      const playerId = snap.docs[0].id;
      await loginRecognizedPlayer(playerId, name);
    } catch {
      setError(lang === "fi" ? "Verkkovirhe. Yritä uudelleen." : "Network error. Try again.");
    }
    setLoading(false);
  }

  // ── Kieliasetus ─────────────────────────────────────────────
  const [lang, setLang] = useState<"fi"|"en">(() =>
    (localStorage.getItem("spinloop_lang") as "fi"|"en") || "fi"
  );
  const [showFaq, setShowFaq] = useState(false);

  function toggleLang() {
    const next = lang === "fi" ? "en" : "fi";
    setLang(next);
    localStorage.setItem("spinloop_lang", next);
  }

  const T = lang === "fi" ? {
    welcome: "Tervetuloa!",
    enterCode: "Syötä klubisi koodi kirjautuaksesi sisään.",
    placeholder: "Klubin koodi, esim. WANHAT",
    pinNext: "PIN-koodi kysytään seuraavaksi.",
    continue: "Jatka →",
    faqTitle: "Ohjeet & UKK",
    faqItems: [
      { q: "Mistä saan klubikoodin?",   a: "Saat klubikoodin järjestäjältä — yleensä sähköpostitse ennen tapahtumaa." },
      { q: "Mikä on tapahtuman PIN?",   a: "Järjestäjä lähettää 4-numeroisen PIN-koodin ennen tapahtuman alkua." },
      { q: "Unohdin PIN-koodini",       a: "Ota yhteyttä klubisi järjestäjään tai adminiin." },
      { q: "Mikä on SPINLOOP?",         a: "Spinloop on pickleball-tapahtumien ja Americano-turnausten hallintajärjestelmä." },
      { q: "Toimiiko se puhelimella?",  a: "Kyllä! Asenna PWA: Safari → Jaa → Lisää Koti-valikkonäyttöön." },
    ],
  } : {
    welcome: "Welcome!",
    enterCode: "Enter your club code to sign in.",
    placeholder: "Club code, e.g. WANHAT",
    pinNext: "The PIN is asked next.",
    continue: "Continue →",
    faqTitle: "Help & FAQ",
    faqItems: [
      { q: "Where do I get the club code?", a: "You receive the club code from the organizer — usually by email before the event." },
      { q: "What is the event PIN?",         a: "The organizer sends a 4-digit PIN before the event starts." },
      { q: "I forgot my PIN",               a: "Contact your club organizer or admin." },
      { q: "What is SPINLOOP?",             a: "Spinloop is a pickleball event and Americano tournament management system." },
      { q: "Does it work on mobile?",       a: "Yes! Install as PWA: Safari → Share → Add to Home Screen." },
    ],
  };

  // ── UI ───────────────────────────────────────────────────
  return (
    <div style={{
      // Sama ilme kuin etusivulla (src/LandingPage.tsx): tumma pohja,
      // haalea kenttäkuva, sininen ja vihreä hehku. Kirjautuminen on
      // sovelluksessa ensimmäinen ruutu, joten ensivaikutelma syntyy tässä.
      minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      background: "#070b12",
      backgroundImage: [
        "radial-gradient(60% 45% at 85% 8%, rgba(29,111,184,0.40) 0%, rgba(29,111,184,0) 70%)",
        "radial-gradient(70% 40% at 50% 100%, rgba(47,143,107,0.25) 0%, rgba(47,143,107,0) 70%)",
        "linear-gradient(180deg, rgba(7,11,18,0.82) 0%, rgba(9,18,34,0.80) 50%, rgba(7,11,18,0.92) 100%)",
        "url('/court-bg.jpg')",
      ].join(", "),
      backgroundSize: "auto, auto, auto, cover", backgroundPosition: "center",
      padding: "calc(24px + env(safe-area-inset-top, 0px)) 20px calc(24px + env(safe-area-inset-bottom, 0px))", fontFamily: F.body,
    }}>
      {/* Logo ja lupaus kortin yläpuolella, kuten etusivulla */}
      <div style={{ textAlign: "center", marginBottom: 22, maxWidth: 380 }}>
        <img src="/logo512.png" alt="Spinloop" width={84} height={84}
          style={{ borderRadius: 20, boxShadow: "0 14px 32px rgba(0,0,0,0.55)", display: "block", margin: "0 auto 14px" }} />
        {step === "club_code" && (
          <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 26, lineHeight: 1.05, color: "#fff" }}>
            {lang === "fi" ? "Pelivuoro turnaukseksi" : "From court time to a tournament"}<br />
            <span style={{ color: "#d9ff3a", textShadow: "0 0 18px rgba(217,255,58,0.45)" }}>
              {lang === "fi" ? "yhdellä napilla." : "in one tap."}
            </span>
          </div>
        )}
      </div>
      <div style={{
        width: "100%", maxWidth: 380,
        background: C.card, borderRadius: 24, padding: 28,
        boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
      }}>
        {/* Klubi + kielivaihto */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.navy, fontFamily: F.heading, letterSpacing: 2 }}>
              <Wordmark />
            </div>
            {club && (
              <div style={{ fontSize: 13, color: C.muted, marginTop: 2, fontWeight: 600 }}>
                {club.name}
              </div>
            )}
          </div>
          <button onClick={toggleLang} style={{
            background: C.bg, border: `1px solid ${C.border}`,
            borderRadius: 16, padding: "5px 12px", color: C.navy,
            fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: F.body,
            flexShrink: 0, marginLeft: 8,
          }}>
            {lang === "fi" ? "🌐 EN" : "🌐 FI"}
          </button>
        </div>

        {/* Virhe */}
        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca",
            borderRadius: 10, padding: "10px 14px", marginBottom: 16,
            fontSize: 13, color: C.error,
          }}>{error}</div>
        )}

        {/* ── VAIHE 1: Klubikoodi ── */}
        {step === "club_code" && (
          <form onSubmit={handleClubCode}>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 6, fontFamily: F.heading }}>
              {T.welcome}
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
              {T.enterCode}
            </div>
            <input
              value={clubCode} onChange={e => setClubCode(e.target.value.toUpperCase())}
              placeholder={T.placeholder}
              style={{ ...inputStyle, marginBottom: 6 }} autoFocus maxLength={20}
            />
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, paddingLeft: 2 }}>
              {T.pinNext}
            </div>
            <button type="submit" style={{ ...btnStyle, opacity: clubCode.length < 3 ? 0.5 : 1 }}
              disabled={loading || clubCode.length < 3}>
              {loading ? (lang === "fi" ? "Haetaan..." : "Searching...") : T.continue}
            </button>

            {/* Passkey — klubikoodia ei tarvita, laite tuntee tunnisteensa */}
            {passkeyReady && (
              <>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, marginTop: 16, textAlign: "center" }}>
                  {lang === "fi" ? "— tai —" : "— or —"}
                </div>
                <button type="button" onClick={() => setStep("player_bio")} style={{
                  ...btnSecondary, background: C.card, boxShadow: "none",
                  border: "1.5px solid #16a34a", color: "#15803d", fontSize: 15,
                }}>
                  🔐 {passkeyName
                    ? (lang === "fi" ? `Kirjaudu — ${passkeyName}` : `Sign in — ${passkeyName}`)
                    : (lang === "fi" ? "Kirjaudu passkeyllä" : "Sign in with a passkey")}
                </button>
              </>
            )}

            {/* Kaveriporukka */}
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>{lang === "fi" ? "— tai —" : "— or —"}</div>
              <button type="button" onClick={() => setStep("quick_event")} style={{
                ...btnSecondary, background: C.card, boxShadow: "none",
                border: `1.5px solid ${C.border}`,
                color: C.navy, fontSize: 14,
              }}>
                {lang === "fi" ? "🤝 Luo kaveriporukka-tapahtuma" : "🤝 Create a friend group event"}
              </button>
            </div>

          </form>
        )}

        {/* ── VAIHE 2: Roolin valinta ── */}
        {step === "role_select" && club && (
          <div>
            {/*
              Tama nakyma kysyi ennen "Admin vai pelaaja?" ja selitti
              pelaajan kirjautumisen viidessa kohdassa. Roolivalinta on
              sisainen jaottelu, jonka joutui lukemaan jokainen — vaikka
              yllapitajia on klubia kohden yksi. Ohjelaatikko taas oli
              oire: kirjautuminen tarvitsi kayttoohjeen.

              Nyt naytetaan se mita ihminen on tullut tekemaan, ja
              yllapito on oma linkkinsa alalaidassa.
            */}
            {/* Klubin nimi on jo logon alla; ei toisteta sita tassa. */}
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 18, fontFamily: F.heading }}>
              {lang === "fi" ? "Kirjaudu pelaajana" : "Sign in as a player"}
            </div>

            {/* Passkey — arkipolku toisesta kerrasta eteenpain */}
            {passkeyReady && (
              <button onClick={() => setStep("player_bio")} style={{
                ...btnStyle, marginBottom: 10, color: "#fff",
                display: "block", textAlign: "left", padding: "14px 16px",
                background: "linear-gradient(135deg, #16a34a, #15803d)", boxShadow: "0 8px 22px rgba(21,128,61,0.3)",
              }}>
                <div style={{ fontSize: 15, marginBottom: 3 }}>
                  🔐 {lang === "fi" ? "Kirjaudu passkeyllä" : "Sign in with a passkey"}
                </div>
                <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.85, lineHeight: 1.4 }}>
                  {passkeyName
                    ? `✓ ${passkeyName} — Touch ID / Face ID`
                    : (lang === "fi" ? "Touch ID, Face ID tai sormenjälki" : "Touch ID, Face ID or fingerprint")}
                </div>
              </button>
            )}

            {/* Edellisen rekisteröinnin epäonnistuminen. Ilman tätä passkey
                vain ei koskaan ilmesty, eikä käyttäjä saa tietää miksi. */}
            {passkeyReady && !passkeyName && passkeyIssue && (
              <div style={{
                background: "#fffbeb", border: "1px solid #fde68a",
                borderRadius: 10, padding: "10px 12px", marginBottom: 10,
                fontSize: 11.5, color: "#92400e", lineHeight: 1.5,
              }}>
                {lang === "fi"
                  ? "Passkeytä ei saatu luotua viime kerralla: "
                  : "The passkey could not be created last time: "}
                {passkeyIssue}
              </div>
            )}

            {/* Tapahtuman PIN — ensimmainen kerta, uusi laite tai vieras */}
            <button onClick={() => setStep("event_pin")} style={{
              ...(passkeyReady ? btnSecondary : btnStyle), marginBottom: 14,
              display: "block", textAlign: "left", padding: "14px 16px",
            }}>
              <div style={{ fontSize: 15, marginBottom: 3 }}>
                <PickleballBall size={18} /> {lang === "fi" ? "Liity tapahtumaan PIN-koodilla" : "Join an event with a PIN"}
              </div>
              <div style={{
                fontSize: 11, fontWeight: 400, lineHeight: 1.4,
                ...(passkeyReady ? { color: C.muted } : { opacity: 0.75 }),
              }}>
                {lang === "fi"
                  ? "Ensimmäinen kerta, uusi laite tai vieraspelaaja. PIN on kutsun sähköpostissa."
                  : "First time, new device or guest player. The PIN is in the invitation email."}
              </div>
            </button>

            <button onClick={() => setStep("club_code")}
              style={{ ...btnSecondary, fontSize: 13, padding: "10px 0", marginBottom: 12 }}>
              ← {lang === "fi" ? "Vaihda klubi" : "Change club"}
            </button>

            {/* Yllapito ei kuulu pelaajan polulle. */}
            <div style={{ textAlign: "center" }}>
              <button onClick={() => setStep("admin_pin")} style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 12, color: C.muted, fontFamily: F.body,
                textDecoration: "underline", padding: "4px 8px",
              }}>
                🔑 {lang === "fi" ? "Ylläpito" : "Admin"}
              </button>
            </div>
          </div>
        )}

        {/* ── VAIHE 3a: Admin PIN ── */}
        {step === "admin_pin" && club && (
          <form onSubmit={handleAdminPin}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6, fontFamily: F.heading }}>
              Admin PIN
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
              {club.name} — {lang === "fi" ? "hallintapaneeli" : "admin panel"}
            </div>
            <input
              value={adminPin} onChange={e => setAdminPin(e.target.value)}
              placeholder={lang === "fi" ? "PIN-koodi" : "PIN code"} type="password"
              style={inputStyle} autoFocus maxLength={10}
            />
            <button type="submit" style={btnStyle} disabled={loading || !adminPin}>
              {loading ? (lang === "fi" ? "Kirjaudutaan..." : "Signing in...") : (lang === "fi" ? "Kirjaudu adminina" : "Sign in as admin")}
            </button>
            <button type="button" onClick={() => setStep("role_select")}
              style={{ ...btnSecondary, marginTop: 10, fontSize: 13, padding: "10px 0" }}>
              ← {lang === "fi" ? "Takaisin" : "Back"}
            </button>
          </form>
        )}

        {step === "super_admin" && (
          <form onSubmit={handleSuperAdminPin}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6, fontFamily: F.heading }}>
              Super admin
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
              {lang === "fi" ? "Klubien hallinta" : "Club management"}
            </div>
            <input
              value={adminPin} onChange={e => setAdminPin(e.target.value)}
              placeholder={lang === "fi" ? "PIN-koodi" : "PIN code"} type="password"
              style={inputStyle} autoFocus maxLength={64}
            />
            <button type="submit" style={btnStyle} disabled={loading || !adminPin}>
              {loading ? (lang === "fi" ? "Kirjaudutaan..." : "Signing in...") : (lang === "fi" ? "Kirjaudu" : "Sign in")}
            </button>
            <button type="button" onClick={() => { setAdminPin(""); setStep("club_code"); }}
              style={{ ...btnSecondary, marginTop: 10, fontSize: 13, padding: "10px 0" }}>
              ← {lang === "fi" ? "Takaisin" : "Back"}
            </button>
          </form>
        )}

        {/* ── VAIHE 3b: Tapahtuman PIN ── */}
        {step === "event_pin" && club && (
          <form onSubmit={handleEventPin}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6, fontFamily: F.heading }}>
              {lang === "fi" ? "Tapahtuman PIN" : "Event PIN"}
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
              {lang === "fi" ? "Pyydä PIN-koodi tapahtuman järjestäjältä." : "Ask the event organizer for your PIN code."}
            </div>
            <input
              value={eventPin} onChange={e => setEventPin(e.target.value.trim())}
              placeholder={lang === "fi" ? "4-numeroinen PIN" : "4-digit PIN"} type="text" inputMode="numeric"
              style={inputStyle} autoFocus maxLength={6}
            />
            <button type="submit" style={btnStyle} disabled={loading || eventPin.length < 4}>
              {loading ? (lang === "fi" ? "Haetaan..." : "Searching...") : (lang === "fi" ? "Jatka →" : "Continue →")}
            </button>
            <button type="button" onClick={() => setStep("role_select")}
              style={{ ...btnSecondary, marginTop: 10, fontSize: 13, padding: "10px 0" }}>
              ← {lang === "fi" ? "Takaisin" : "Back"}
            </button>
          </form>
        )}

        {/* ── VAIHE 4: Pelaajan nimi ── */}
        {step === "player_name" && club && foundEvent && (
          <form onSubmit={handlePlayerName}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6, fontFamily: F.heading }}>
              {lang === "fi" ? "Mikä on nimesi?" : "What's your name?"}
            </div>
            {foundEvent.venue && (
              <div style={{ fontSize: 12, color: C.gold, marginBottom: 16 }}>
                📍 {foundEvent.venue}
              </div>
            )}
            <input
              value={playerName}
              onChange={e => { setPlayerName(e.target.value); setConfirmNewPlayer(false); }}
              placeholder={lang === "fi" ? "Etunimi riittää" : "First name is enough"}
              style={inputStyle} autoFocus
            />

            {/* Tarkoititko jotakuta näistä? Ehdotukset syntyvät vain
                kirjoitetusta tekstistä, joten koko jäsenlistaa ei
                näytetä kenellekään. */}
            {(() => {
              const hits = suggestPlayers(playerName, clubPlayers);
              if (!hits.length) return null;
              return (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
                    {lang === "fi" ? "Tarkoititko:" : "Did you mean:"}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {hits.map(p => (
                      <button
                        key={p.id} type="button" disabled={loading}
                        onClick={() => joinEvent(p.id, p.name)}
                        style={{
                          padding: "8px 14px", borderRadius: 20, cursor: "pointer",
                          border: `1px solid ${C.gold}`, background: "rgba(244,164,38,0.12)",
                          color: C.text, fontSize: 13, fontWeight: 600, fontFamily: F.body,
                        }}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {confirmNewPlayer ? (
              <div style={{
                fontSize: 12, color: C.text, marginBottom: 16, lineHeight: 1.6,
                background: "rgba(244,164,38,0.12)", border: `1px solid ${C.gold}`,
                borderRadius: 10, padding: "10px 12px",
              }}>
                {lang === "fi"
                  ? <>Nimeä <strong>{playerName.trim()}</strong> ei löydy klubista. Jos olet pelannut aiemmin toisella nimellä, valitse se yltä tai korjaa kirjoitusasu — muuten sinulle luodaan uusi pelaaja.</>
                  : <>The name <strong>{playerName.trim()}</strong> is not in this club. If you've played before under another name, pick it above or fix the spelling — otherwise a new player will be created for you.</>}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 16, lineHeight: 1.5 }}>
                {lang === "fi"
                  ? "Jos olet pelannut aiemmin, profiilisi löytyy automaattisesti nimesi perusteella. Seuraavalla kerralla voit kirjautua sormenjäljellä."
                  : "If you've played before, your profile is found automatically by name. Next time you can sign in with fingerprint."}
              </div>
            )}

            <button type="submit" style={btnStyle} disabled={loading || playerName.trim().length < 2}>
              {loading
                ? (lang === "fi" ? "Kirjaudutaan..." : "Signing in...")
                : confirmNewPlayer
                  ? (lang === "fi" ? "Luo uusi pelaaja ✓" : "Create new player ✓")
                  : (lang === "fi" ? "Vahvista osallistuminen ✓" : "Confirm participation ✓")}
            </button>
          </form>
        )}

        {/* ── VAIHE: Kaveriporukka ── */}
        {step === "quick_event" && (
          <QuickEventForm onSuccess={onSuccess} onBack={() => setStep("club_code")} lang={lang} />
        )}

        {/* ── VAIHE: Biometria ── */}
        {step === "player_bio" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔐</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 6, fontFamily: F.heading }}>
              {passkeyName
                ? `${lang === "fi" ? "Hei" : "Hi"}, ${passkeyName}!`
                : (lang === "fi" ? "Kirjaudu passkeyllä" : "Sign in with a passkey")}
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
              {lang === "fi"
                ? "Käytä Touch ID:tä, Face ID:tä tai Windows Helloa. Puhelin löytää oikean tunnisteen automaattisesti."
                : "Use Touch ID, Face ID or Windows Hello. Your device finds the right credential automatically."}
            </div>
            {error && <div style={{ fontSize: 12, color: C.error, marginBottom: 12 }}>{error}</div>}
            <button onClick={handlePasskeyLogin} style={{ ...btnStyle, marginBottom: 12, color: "#fff", background: "linear-gradient(135deg,#16a34a,#15803d)", boxShadow: "0 8px 22px rgba(21,128,61,0.3)" }} disabled={loading}>
              {loading
                ? (lang === "fi" ? "Tunnistetaan..." : "Authenticating...")
                : "🔐 " + (lang === "fi" ? "Tunnistaudu" : "Authenticate")}
            </button>
            <button onClick={() => { setError(""); setStep(club ? "event_pin" : "club_code"); }}
              style={{ ...btnSecondary, fontSize: 13, padding: "10px 0" }}>
              {lang === "fi" ? "Käytä PIN-koodia" : "Use PIN code"}
            </button>
          </div>
        )}
      </div>

      {/* Kysy Spinloopilta: sama UKK-chat kuin etusivulla, kortin alla
          tummalla pohjalla. Korvaa aiemman "Ohjeet & UKK" -haitarin. */}
      {step === "club_code" && (
        <div style={{ width: "100%", maxWidth: 380, marginTop: 18 }}>
          {!showFaq ? (
            <button type="button" onClick={() => setShowFaq(true)} style={{
              width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)",
              color: "#fff", borderRadius: 14, padding: "12px 14px", fontSize: 14, fontWeight: 600,
              fontFamily: F.body, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              💬 {LANDING_TEXT[lang].faqTitle}
            </button>
          ) : (
            <FaqChat t={LANDING_TEXT[lang]} />
          )}
        </div>
      )}
    </div>
  );
}

// ── QuickEventForm — Kaveriporukka ilman klubia ──────────────
// Spinloop on pickleball-sovellus — laji on kiintea uusissa tapahtumissa.
// Kannassa olevat vanhat padel/tennis-tapahtumat toimivat edelleen; niiden
// laji vain luetaan sellaisenaan eika sita voi enaa valita.

interface QuickEventFormProps {
  onSuccess: (session: AuthSession) => void;
  onBack: () => void;
  lang: "fi"|"en";
}

function QuickEventForm({ onSuccess, onBack, lang }: QuickEventFormProps) {
  const [name,    setName]    = useState("");
  const [venue,   setVenue]   = useState("");
  // datetime-local odottaa paikallista aikaa muodossa YYYY-MM-DDTHH:mm.
  // toISOString() antaa UTC:n, jolloin oletusaika oli Suomessa kolme
  // tuntia pielessä — sama vika korjattiin AdminView'n luontilomakkeesta.
  const [date,    setDate]    = useState(() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [panos,   setPanos]   = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError(lang === "fi" ? "Syötä nimesi." : "Please enter your name."); return; }
    setError("");
    setLoading(true);

    try {
      await ensureAuth();
      // Väliaikainen klubi luodaan palvelimella: klubien luonti on
      // säännöissä varattu super adminille, ja PIN-koodit kuuluvat
      // salaiseen alikokoelmaan eivät klubidokumenttiin.
      const res = await createQuickEventFn({
        name: name.trim(),
        venue: venue.trim(),
        // Palvelimelle yksiselitteinen ISO-aika, ei paikallista merkkijonoa.
        datetime: new Date(date).toISOString(),
        panos: panos ? Number(panos) : 0,
        lang,
      });
      const { token, club, adminPin, eventPin } = res.data;
      await signInWithClubToken(token);

      // Tallenna tiedot localStorageen
      localStorage.setItem("spinloop_club_code", club.clubCode);
      localStorage.setItem("spinloop_club_name", club.name);
      localStorage.setItem("spinloop_admin_pin", adminPin);

      onSuccess({
        role: "club_admin",
        clubId: club.id,
        clubCode: club.clubCode,
        clubName: club.name,
        _quickAdminPin: adminPin,
        _quickEventPin: eventPin,
      } as AuthSession & { _quickAdminPin: string; _quickEventPin: string });

    } catch (err) {
      setError(lang === "fi" ? "Virhe luodessa tapahtumaa. Yritä uudelleen." : "Error creating event. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, padding: 0, color: C.muted }}>←</button>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text, fontFamily: F.heading }}>🤝 {lang === "fi" ? "Kaveriporukka" : "Friend group"}</div>
          <div style={{ fontSize: 12, color: C.muted }}>{lang === "fi" ? "Luo tapahtuma — jaa koodi kavereille" : "Create an event — share the code with friends"}</div>
        </div>
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: C.error }}>
          {error}
        </div>
      )}

      <form onSubmit={handleCreate}>
        <input value={name} onChange={e => setName(e.target.value)}
          placeholder={lang === "fi" ? "Sinun nimesi *" : "Your name *"} style={inputStyle} autoFocus />

        {/* Lajivalitsin poistettu — Spinloop on pickleball-sovellus.
            Uudet tapahtumat luodaan aina lajilla "pickleball". */}

        <input value={venue} onChange={e => setVenue(e.target.value)}
          placeholder={lang === "fi" ? "Paikka (esim. Puistokentät)" : "Venue (e.g. City Park Courts)"} style={inputStyle} />

        <input value={date} onChange={e => setDate(e.target.value)}
          type="datetime-local" style={inputStyle} />

        <input value={panos} onChange={e => setPanos(e.target.value)}
          placeholder={lang === "fi" ? "Panos € (jätä tyhjäksi jos ilmainen)" : "Entry fee € (leave empty if free)"} type="number" style={inputStyle} />

        <button type="submit" disabled={loading || !name.trim()} style={{
          ...btnStyle, opacity: loading || !name.trim() ? 0.5 : 1,
        }}>
          {loading ? (lang === "fi" ? "Luodaan..." : "Creating...") : "🚀 " + (lang === "fi" ? "Luo tapahtuma" : "Create event")}
        </button>
      </form>
    </div>
  );
}
